// ============================================================================
// LES DÉCISIONS DE LA GRILLE « PRIX & MARGES » — pures, extraites de l'écran pour être testées.
//
// UTILITÉ : l'écran ne décide que de trois choses — une ligne est-elle éditable, que vaut la saisie
// en cours, et de quel côté de la cible tombe le coefficient. Tout le CHIFFRE est délégué à
// `prix-derive.ts` (les mêmes fonctions que la fiche) ; ici ne vivent que ces décisions d'écran,
// dans un module sans React, comme les autres logiques d'écran du paquet (`import-masque`,
// `etiquettes-print`…).
// ============================================================================
import { parseMinor } from '@orgazone/money-format';
import type { ArticleModel } from '../api';
import { achatTheoriqueCorner, coeffCentsFromBp, indicateursPrix, type IndicateursPrix } from './prix-derive';
import { t } from '../i18n';

/**
 * Écart au coefficient cible au-delà duquel la marge se signale, en CENTIÈMES de coefficient
 * (±0,15, seuil de la maquette). Sur des entiers : aucun flottant n'entre dans la comparaison.
 * ⚠️ Il porte sur l'ÉCART À LA CIBLE, pas sur la marge : 40 % n'est ni bon ni mauvais dans
 * l'absolu — il l'est par rapport à ce qui a été négocié.
 */
export const TOLERANCE_COEF_CENTS = 15;

export type Ecart = 'low' | 'high' | 'ok';

/** De quel côté de la cible un coefficient tombe — `null` quand l'un des deux manque. */
export function ecartALaCible(coeffCents: number | null, cibleCents: number | null): Ecart | null {
  if (coeffCents == null || cibleCents == null) return null;
  const d = coeffCents - cibleCents;
  return d < -TOLERANCE_COEF_CENTS ? 'low' : d > TOLERANCE_COEF_CENTS ? 'high' : 'ok';
}

/** Ce que la ligne sait d'elle-même AVANT toute saisie — l'éditabilité et la cible, pas les chiffres. */
export interface MetaLigne {
  /** Pourquoi la ligne n'est pas éditable, en clair — `null` quand elle l'est. */
  blocage: string | null;
  /** La cible contractuelle en CENTIÈMES de coefficient (même unité que `coeffCents`) — pour la comparer et l'afficher. */
  cibleCents: number | null;
  /** La même cible en POINTS DE BASE, telle que le contrat la porte — pour en calculer un prix sans l'arrondir d'abord. */
  cibleBp: number | null;
  /** Taux exploitable pour dériver le HT — 0 est le défaut de création, donc « pas renseigné ». */
  vatBp: number | null;
}

/**
 * L'éditabilité et la cible d'un modèle. Décision d'ÉCRAN — l'arithmétique, elle, est déléguée.
 *
 * ⚠️ UN MODÈLE ENTIÈREMENT DÉSACTIVÉ NE S'ÉDITE PAS : ses agrégats de prix (filtrés sur l'actif)
 * sont NULS, que le mapping rend 0 — et « 0,00 € » éditable sur un modèle mort inviterait à poser
 * un prix que personne ne verra. La ligne renvoie à la fiche, où l'on réactive d'abord.
 */
export function metaDe(a: ArticleModel): MetaLigne {
  const blocage = a.activeCount === 0
    ? t('Modèle entièrement désactivé')
    : a.vatMixed
      ? t('Plusieurs taux de TVA sur ce modèle')
      : a.priceMinCents !== a.priceMaxCents
        ? t('Prix différents selon la déclinaison')
        : null;
  return {
    blocage,
    cibleCents: coeffCentsFromBp(a.supplierCoefficientBp),
    cibleBp: a.supplierCoefficientBp != null && a.supplierCoefficientBp > 0 ? a.supplierCoefficientBp : null,
    // ⚠️ 0 % EST UN TAUX (un article exonéré), pas une absence : seul `null` dit « inconnu ».
    vatBp: a.vatRateBp ?? null,
  };
}

/** Ce que la ligne AFFICHE pour un TTC donné — indicateurs, prix d'achat, et sa nature. */
export interface AffichageLigne {
  ind: IndicateursPrix | null;
  paFine: number | null;
  /** `true` = prix d'achat DÉDUIT du contrat (corner), à marquer « ≈ » — jamais un coût constaté. */
  paTheorique: boolean;
}

/**
 * Les indicateurs d'une ligne pour un TTC donné — corner compris, par les MÊMES fonctions que la fiche.
 *
 * ⚠️ EN CORNER le coefficient n'est PAS dérivé : il EST la clause, et le prix d'achat s'en déduit
 * (`achatTheoriqueCorner`). Dériver le coefficient du tarif y afficherait la marge du régime FERME
 * sous un modèle corner — plausible, faux, invisible (même règle que la fiche, article-prix.tsx).
 * ⚠️ RÉGIMES MÊLÉS = pas d'indicateurs : la moitié du modèle vit sous un régime, l'autre sous
 * l'autre, et un chiffre unique ne décrirait ni l'une ni l'autre. Le tarif, lui, reste montré.
 */
export function indicateursLigne(a: ArticleModel, meta: MetaLigne, ttcCents: number | null): AffichageLigne {
  if (ttcCents == null || meta.vatBp == null || a.regimeMixed) {
    return { ind: null, paFine: a.costMinFine > 0 ? a.costMinFine : null, paTheorique: false };
  }
  if (a.purchaseRegime === 'corner') {
    const coeffBp = a.supplierCoefficientBp;
    const paFine = achatTheoriqueCorner(ttcCents, coeffBp);
    return { ind: indicateursPrix(ttcCents, paFine, meta.vatBp, coeffBp), paFine, paTheorique: true };
  }
  const paFine = a.costMinFine > 0 ? a.costMinFine : null;
  return { ind: indicateursPrix(ttcCents, paFine, meta.vatBp), paFine, paTheorique: false };
}

/** Ce qu'une saisie vaut : intacte (dont « revenue au prix d'origine »), valide, ou illisible. */
export interface SaisieLue {
  etat: 'intact' | 'valide' | 'illisible';
  /** Les centimes exploitables pour DÉRIVER — le prix d'origine quand la saisie est intacte. */
  cents: number | null;
  /** Le motif d'un refus, dans les mots de `parseMinor` — à montrer, jamais à réécrire. */
  motif?: string;
}

/**
 * Lit une saisie de prix face au prix d'origine.
 *
 * ⚠️ LA SAISIE EST GARDÉE BRUTE PAR L'ÉCRAN, et c'est ICI qu'elle se parse (`parseMinor`, jamais un
 * `Number()` maison) : la première version stockait des centimes et re-rendait le champ reformaté à
 * chaque frappe — taper « 6 » affichait « 6,00 », et « 69,99 » devenait insaisissable.
 * ⚠️ REVENIR AU PRIX D'ORIGINE REND LA LIGNE INTACTE : sinon « 1 prix modifié » resterait affiché
 * sur une valeur identique à celle en base.
 */
export function litSaisie(brut: string | undefined, prixOrigine: number): SaisieLue {
  if (brut === undefined) return { etat: 'intact', cents: prixOrigine };
  const lu = parseMinor(brut, { scale: 2 });
  if (lu.state === 'ok' && lu.minor >= 0) {
    return lu.minor === prixOrigine ? { etat: 'intact', cents: prixOrigine } : { etat: 'valide', cents: lu.minor };
  }
  return { etat: 'illisible', cents: null, motif: lu.state === 'rejected' ? lu.reason : t('un prix ne peut pas être vide ni négatif') };
}
